// Downloadbaar PDF-bestand voor het managementverslag (kort én uitgebreid) via jsPDF + autotable.
// Autotable pagineert automatisch en splitst nooit een tabelrij over twee pagina's — pagina-veilig.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const BLUE = [28, 100, 176];
const GREEN = [5, 150, 105];
const RED = [225, 29, 72];
const AMBER = [217, 119, 6];
const SLATE = [100, 116, 139];
const uur = (h) => (Math.round((Number(h) || 0) * 10) / 10).toFixed(1).replace(".", ",") + " u";

export async function downloadReportPDF(filename, model, { kort } = {}) {
  const { periode, week, nu, logo, kpis, kostenUit, kostprijsOpbouw, kostprijsTrend, producten, lijnen, perDag, vergelijking, vierWeken, trends, inkoop, inkoopHulp, afkeur, afkeurTot, personeel, voorraad, manual, opt, ondertekening, prodKg, toonOmzetProduct, euro, kgf, pct } = model;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 12;
  const S = kort ? { dashboard: true } : (opt.secties || {});
  const toonNormen = !kort && !!S.normen;
  const N = (manual && manual.normen) || {};
  const man = manual || {};

  const drawHeader = () => {
    if (logo) { try { doc.addImage(logo, "PNG", marginX, 8, 16, 16); } catch (e) { /* logo optioneel */ } }
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(15, 23, 42);
    doc.text("Clazing Convenience Food B.V.", logo ? marginX + 20 : marginX, 14);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
    doc.text(kort ? "Managementverslag — kort" : "Managementverslag — uitgebreide sturingsrapportage", logo ? marginX + 20 : marginX, 19);
    doc.text(`${periode} · ${nu}`, pageW - marginX, 14, { align: "right" });
    doc.setDrawColor(28, 100, 176); doc.setLineWidth(0.6); doc.line(marginX, 25, pageW - marginX, 25);
  };

  let y = 30;
  const tbl = (opts) => {
    autoTable(doc, {
      startY: y, margin: { left: marginX, right: marginX, top: 28 },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5, cellPadding: 1.6, textColor: [15, 23, 42] },
      styles: { lineColor: [226, 232, 240], lineWidth: 0.1, overflow: "linebreak" },
      didDrawPage: drawHeader,
      pageBreak: "avoid",
      ...opts,
    });
    y = doc.lastAutoTable.finalY + 5;
  };
  const PAGE_BOTTOM = 285;
  // estRows = verwacht aantal tabelrijen dat ONDER de koptekst komt. Zo houden we
  // de koptekst bij zijn tabel: past het blok niet meer onderaan, maar wel op een
  // verse pagina, dan verhuist de koptekst mee naar het volgende blad.
  const heading = (txt, estRows = 3) => {
    const tableH = 10 + Math.max(estRows, 1) * 6.6; // kolomkop + rijen
    const blockH = 9 + tableH;                       // koptekst + begin tabel
    if (blockH <= PAGE_BOTTOM - 30 && y + blockH > PAGE_BOTTOM) { doc.addPage(); y = 30; }
    else if (y > 248) { doc.addPage(); y = 30; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(28, 100, 176);
    doc.text(txt, marginX, y); y += 1.5;
    doc.setDrawColor(28, 100, 176); doc.setLineWidth(0.4); doc.line(marginX, y, pageW - marginX, y); y += 4;
  };
  const paragraph = (label, txt) => {
    if (y > 258) { doc.addPage(); y = 30; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(28, 100, 176);
    doc.text(label, marginX, y); y += 4;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(30, 41, 59);
    const lines = doc.splitTextToSize(txt && txt.trim() ? txt : "—", pageW - 2 * marginX);
    if (y + lines.length * 4.4 > 285) { doc.addPage(); y = 30; }
    doc.text(lines, marginX, y); y += lines.length * 4.4 + 3;
  };
  const toel = (key) => { const t = (man.toelichting || {})[key]; if (t && String(t).trim()) paragraph("Toelichting", String(t)); };

  drawHeader();
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(28, 100, 176);
  doc.text(`Managementverslag ${periode}`, marginX, y); y += 7;

  // ---- 1. KPI-dashboard ----
  const pv = vergelijking ? vergelijking.prev : {};
  const vw = vierWeken || {};
  const statColor = (val, norm, lower) => {
    if (!toonNormen || norm == null || norm === "" || val == null) return null;
    const n = Number(norm); if (!isFinite(n) || n === 0) return null;
    const better = lower ? val <= n : val >= n;
    if (better) return GREEN;
    return Math.abs((val - n) / Math.abs(n)) <= 0.05 ? AMBER : RED;
  };
  const kpiRows = [];
  const addK = (label, val, fmt, { norm, lower, prev, avg4 } = {}) => {
    const valTxt = val == null ? "—" : (fmt ? fmt(val) : String(val));
    const prevTxt = prev == null || val == null || !fmt ? "—" : `${val - prev > 0 ? "+" : ""}${fmt(val - prev)}`;
    kpiRows.push({ c: [label, valTxt, toonNormen ? (norm != null && norm !== "" ? (fmt ? fmt(Number(norm)) : String(norm)) : "—") : null, prevTxt, avg4 != null && fmt ? fmt(avg4) : "—"], _st: statColor(typeof val === "number" ? val : null, norm, lower) });
  };
  addK("Geproduceerd", kpis.kg, kgf, { norm: N.kg, lower: false, prev: pv.kg, avg4: vw.kg });
  addK("Omzet (week)", kpis.omzetIngevuld ? kpis.omzet : null, euro, { prev: pv.omzet, avg4: vw.omzet });
  addK("Marge €", kpis.omzetIngevuld ? kpis.marge : null, euro, { prev: pv.marge, avg4: vw.marge });
  addK("Marge %", kpis.omzetIngevuld ? kpis.margePct : null, pct, { norm: N.margePct, lower: false });
  addK("Kostprijs / kg", kpis.kostprijsKg, euro, { norm: N.kostprijsKg, lower: true, prev: pv.kostprijsKg, avg4: vw.kostprijsKg });
  addK("Yield", kpis.yieldPct, pct, { norm: N.yieldPct, lower: false, prev: pv.yieldPct, avg4: vw.yieldPct });
  addK("Uitval", kpis.uitvalPct, pct, { norm: N.uitvalPct, lower: true, prev: pv.uitvalPct, avg4: vw.uitvalPct });
  addK("Gem. OEE", kpis.oee, pct, { norm: N.oee, lower: false, prev: pv.oee, avg4: vw.oee });
  addK("kg / productie-uur", kpis.kgPerProdUur, (v) => kgf(v) + "/u", { norm: N.kgPerUur, lower: false });
  addK("Personeelskosten", personeel.totaal.werkKost, euro, { prev: pv.persKost });
  addK("Pers.kosten / kg", kpis.kg > 0 ? personeel.totaal.werkKost / kpis.kg : null, euro, { norm: N.persKostPerKg, lower: true });
  addK("Gewerkt / gepland", `${uur(personeel.totaal.werkUren)} / ${uur(personeel.totaal.planUren)}`, null, {});
  if (man.pers) {
    if (man.pers.overurenUur !== "" && man.pers.overurenUur != null) addK("Overuren", uur(Number(man.pers.overurenUur)), null, {});
    if (man.pers.ziekteverzuim !== "" && man.pers.ziekteverzuim != null) addK("Ziekteverzuim", Number(man.pers.ziekteverzuim), pct, {});
    if (man.pers.fte !== "" && man.pers.fte != null) addK("Actieve FTE", man.pers.fte, null, {});
    if (man.pers.uzkAantal !== "" && man.pers.uzkAantal != null) addK("Uitzendkrachten", man.pers.uzkAantal, null, {});
  }
  heading(kort ? "Kerncijfers week" : "1 · Managementsamenvatting");
  const head1 = toonNormen ? [["KPI", "Waarde", "Norm", "vs vorige", "4-wk gem."]] : [["KPI", "Waarde", "vs vorige", "4-wk gem."]];
  tbl({
    head: head1,
    body: kpiRows.map((r) => (toonNormen ? r.c : [r.c[0], r.c[1], r.c[3], r.c[4]])),
    columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "right" }, [toonNormen ? 2 : 2]: { halign: "right" }, [toonNormen ? 3 : 3]: { halign: "right" }, [toonNormen ? 4 : 3]: { halign: "right" } },
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index === 1) { const st = kpiRows[d.row.index]._st; if (st) { d.cell.styles.textColor = st; d.cell.styles.fontStyle = "bold"; } }
    },
  });

  if (kort) {
    // Korte versie: product, lijn, per dag, vergelijking, inkoop, personeel, afkeur
    heading("Productie per product", producten.length);
    tbl({
      head: [["Product", "kg", "Uitval", "OEE", "Kostprijs/kg", "Kosten", ...(toonOmzetProduct ? ["Omzet"] : [])]],
      body: producten.map((p) => [p.product, kgf(p.kg), pct(p.uitvalPct), pct(p.oee), euro(p.kostprijsKg), euro(p.kosten), ...(toonOmzetProduct ? [euro(p.omzet)] : [])]),
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
    });
    heading("Productie per lijn", lijnen.length);
    tbl({
      head: [["Lijn", "kg", "Uitval", "OEE"]],
      body: lijnen.map((l) => [l.lijn, kgf(l.kg), pct(l.uitvalPct), pct(l.oee)]),
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
    });
    if (opt.secties?.personeel !== false) persSection();
    if (opt.secties?.afkeur !== false) afkeurSection();
    finalize();
    return;
  }

  // ---- Uitgebreid ----
  // 2. Productie
  if (S.productie !== false) {
    heading("2 · Productie", producten.length);
    tbl({
      head: [["Product", "kg", "Yield", "Uitval", "OEE", "kg/uur", "Kostprijs/kg", "Kosten", ...(toonOmzetProduct ? ["Omzet"] : [])]],
      body: producten.map((p) => [p.product, kgf(p.kg), pct(p.yieldPct), pct(p.uitvalPct), pct(p.oee), p.prodUren > 0 ? kgf(p.kgPerUur) + "/u" : "—", euro(p.kostprijsKg), euro(p.kosten), ...(toonOmzetProduct ? [euro(p.omzet)] : [])]),
      columnStyles: Object.fromEntries([1, 2, 3, 4, 5, 6, 7, 8].map((i) => [i, { halign: "right" }])),
    });
    tbl({
      head: [["Lijn", "kg", "Yield", "Uitval", "OEE", "Prod.uren", "Stilstand", "kg/uur"]],
      body: lijnen.map((l) => [l.lijn, kgf(l.kg), pct(l.yieldPct), pct(l.uitvalPct), pct(l.oee), uur(l.prodUren), uur(l.stilstandUur), l.prodUren > 0 ? kgf(l.kgPerUur) + "/u" : "—"]),
      columnStyles: Object.fromEntries([1, 2, 3, 4, 5, 6, 7].map((i) => [i, { halign: "right" }])),
    });
    if (S.grafiek !== false && perDag && perDag.some((d) => d.kg > 0)) {
      tbl({ head: [["Dag", "Productie (kg)"]], body: perDag.map((d) => [d.label, kgf(d.kg)]), columnStyles: { 1: { halign: "right" } } });
    }
    toel("productie");
  }
  // 3. Yield
  if (S.yield !== false) {
    heading("3 · Yield & rendement", producten.length);
    const rows = producten.map((p) => {
      const ny = Number(p.normYield);
      const norm = (p.normYield != null && isFinite(ny) && ny > 0) ? ny : null;
      const verschil = (norm != null && isFinite(Number(p.yieldPct))) ? p.yieldPct - norm : null;
      return { p, norm, verschil, kgImpact: verschil != null ? (p.kg + p.uitvalKg) * verschil : null };
    }).sort((a, b) => (a.verschil ?? 0) - (b.verschil ?? 0));
    tbl({
      head: [["Product", "Yield", "Norm", "Verschil", "≈ kg-impact"]],
      body: rows.map((r) => [r.p.product, pct(r.p.yieldPct), r.norm == null ? "—" : pct(r.norm), r.verschil == null ? "—" : pct(r.verschil), r.kgImpact == null ? "—" : kgf(r.kgImpact)]),
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
      didParseCell: (d) => { if (d.section === "body" && (d.column.index === 3 || d.column.index === 4)) { const r = rows[d.row.index]; const v = d.column.index === 3 ? r.verschil : r.kgImpact; if (v != null) d.cell.styles.textColor = v >= 0 ? GREEN : RED; } },
    });
    toel("yield");
  }
  // 4. Financieel
  if (S.financieel !== false) {
    heading("4 · Financieel", 6);
    const fb = [["Omzet week", kpis.omzetIngevuld ? euro(kpis.omzet) : "niet ingevuld"], ["Omzet per geproduceerde kg", kpis.omzetIngevuld && kpis.kg > 0 ? euro(kpis.omzet / kpis.kg) : "—"], ["Kostprijs per kg", euro(kpis.kostprijsKg)], ["Brutomarge €", kpis.omzetIngevuld ? euro(kpis.marge) : "—"], ["Brutomarge %", kpis.omzetIngevuld ? pct(kpis.margePct) : "—"]];
    if (man.financieel && man.financieel.budget) fb.push(["Omzet vs budget", euro(Number(man.financieel.budget))]);
    tbl({ body: fb, columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "right" } } });
    toel("financieel");
  }
  // 5. Kosten
  if (S.kosten !== false) {
    heading("5 · Kosten", 8);
    const ku = kostenUit || {};
    const mk = man.kosten || {};
    const extra = [["verpakking", "Verpakking"], ["transport", "Transport"], ["onderhoud", "TD / onderhoud"], ["uzk", "Uitzendkrachten"], ["overigExtra", "Overige productiekosten"]].filter(([k]) => mk[k] !== "" && mk[k] != null).map(([k, l]) => [l, Number(mk[k])]);
    const base = [["Grondstoffen", ku.grondstof || 0], ["Hulpstoffen", ku.hulpstof || 0], ["Personeel (productie)", ku.arbeid || 0], ["Stikstof", ku.stikstof || 0], ["Overige (ERP)", ku.overig || 0], ...extra];
    const totaal = (ku.totaal || kpis.kosten) + extra.reduce((s, x) => s + x[1], 0);
    const aandeel = S.kostenaandeel !== false;
    const aand = (v) => totaal > 0 ? pct(v / totaal) : "—";
    tbl({
      head: [aandeel ? ["Kostengroep", "Werkelijk", "€/kg", "% aandeel"] : ["Kostengroep", "Werkelijk", "€/kg"]],
      body: [
        ...base.map(([l, v]) => aandeel ? [l, euro(v), kpis.kg > 0 ? euro(v / kpis.kg) : "—", aand(v)] : [l, euro(v), kpis.kg > 0 ? euro(v / kpis.kg) : "—"]),
        aandeel
          ? [{ content: "Totaal productiekosten", styles: { fontStyle: "bold" } }, { content: euro(totaal), styles: { fontStyle: "bold", halign: "right" } }, { content: kpis.kg > 0 ? euro(totaal / kpis.kg) : "—", styles: { fontStyle: "bold", halign: "right" } }, { content: totaal > 0 ? "100,0%" : "—", styles: { fontStyle: "bold", halign: "right" } }]
          : [{ content: "Totaal productiekosten", styles: { fontStyle: "bold" } }, { content: euro(totaal), styles: { fontStyle: "bold", halign: "right" } }, { content: kpis.kg > 0 ? euro(totaal / kpis.kg) : "—", styles: { fontStyle: "bold", halign: "right" } }],
      ],
      columnStyles: aandeel ? { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } } : { 1: { halign: "right" }, 2: { halign: "right" } },
    });
    toel("kosten");
  }
  // 6. Personeel
  if (S.kostprijsopbouw !== false && !kort) {
    heading("Kostprijsopbouw per product", (kostprijsOpbouw || []).length || 1);
    const ko = kostprijsOpbouw || [];
    tbl({
      head: [["Product", "kg", "Grondstof/kg", "Hulpstof/kg", "Personeel/kg", "Stikstof/kg", "Overig/kg", "Kostprijs/kg"]],
      body: ko.length ? ko.map((p) => [p.product, kgf(p.kg), p.kg > 0 ? euro(p.grondstof / p.kg) : "—", p.kg > 0 ? euro(p.hulpstof / p.kg) : "—", p.kg > 0 ? euro(p.arbeid / p.kg) : "—", p.kg > 0 ? euro(p.stikstof / p.kg) : "—", p.kg > 0 ? euro(p.overig / p.kg) : "—", euro(p.kostprijsKg)]) : [[{ content: "Geen productie.", colSpan: 8, styles: { textColor: SLATE } }]],
      columnStyles: Object.fromEntries([1, 2, 3, 4, 5, 6, 7].map((i) => [i, { halign: "right" }])),
    });
    const kt = kostprijsTrend || { weken: [], producten: [] };
    if ((kt.weken || []).length && (kt.producten || []).length) {
      tbl({
        head: [["Kostprijs/kg per product", ...kt.weken.map((w) => "wk " + w)]],
        body: kt.producten.map((p) => [p.product, ...p.waarden.map((v) => v == null ? "—" : euro(v))]),
        columnStyles: Object.fromEntries(kt.weken.map((_, i) => [i + 1, { halign: "right" }])),
      });
    }
    const hfItems = ko.filter((p) => p.halffab);
    if (hfItems.length) {
      heading("Opbouw halffabrikaat-producten (tumblen/mengen)", hfItems.length);
      tbl({
        head: [["Product", "Halffabrikaat", "Grondstof/kg", "Hulpstof (tumblen/mengen)/kg", "Halffab-kostprijs/kg"]],
        body: hfItems.map((p) => [p.product, p.halffab.bronWeek && p.halffab.bronWeek !== week ? `${p.halffab.naam} (wk ${p.halffab.bronWeek})` : p.halffab.naam, euro(p.halffab.grondstof), euro(p.halffab.hulpstof), euro(p.halffab.totaal)]),
        columnStyles: { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
      });
    }
    toel("kostprijsopbouw");
  }
  if (S.personeel !== false) { heading("6 · Personeel", (personeel.bureaus || []).length + 2); persSection(); persExtraSection(); toel("personeel"); }
  // 7. Kwaliteit
  if (S.kwaliteit !== false) {
    heading("7 · Kwaliteit", 6);
    const kw = man.kwaliteit || {};
    const kb = [["Afkeur (uit systeem)", kgf(afkeurTot || 0)], ["Aantal klachten", kw.klachten || "0"], ["Interne afwijkingen", kw.afwijkingen || "0"], ["Rework", kw.rework || "0"], ["Geblokkeerde producten", kw.geblokkeerd || "0"], ["Microbiologische afwijkingen", kw.micro || "0"]];
    tbl({ body: kb, columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "right" } } });
    if (kw.qaActies) paragraph("Openstaande QA-acties", kw.qaActies);
    if (kw.audits) paragraph("Audits / bijzonderheden", kw.audits);
    toel("kwaliteit");
  }
  // 8. Techniek
  if (S.techniek !== false) {
    heading("8 · Technische dienst", 6);
    const td = man.techniek || {};
    const autoStil = lijnen.reduce((s, l) => s + (l.stilstandUur || 0), 0);
    const tb = [["Geregistreerde stilstand (ERP)", uur(autoStil)]];
    [["storingen", "Aantal storingen"], ["stilstandUur", "Totale stilstand (u)"], ["openReparaties", "Openstaande reparaties"], ["preventief", "Preventief onderhoud"]].forEach(([k, l]) => { if (td[k] !== "" && td[k] != null) tb.push([l, String(td[k])]); });
    if (td.kostenTD !== "" && td.kostenTD != null) tb.push(["Kosten TD/onderhoud", euro(Number(td.kostenTD))]);
    tbl({ body: tb, columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "right" } } });
    if (td.hoofdstoring) paragraph("Belangrijkste storing(en)", td.hoofdstoring);
    if (td.punten) paragraph("Technische punten die productie beïnvloeden", td.punten);
    toel("techniek");
  }
  // 9. Voorraad & inkoop
  if (S.voorraad !== false) {
    heading("9 · Voorraad & inkoop", 3);
    const vr = voorraad || { kritisch: [], grondTot: 0, hulpTot: 0, prijsOntw: [] };
    tbl({ body: [["Voorraadwaarde grondstoffen", euro(vr.grondTot || 0)], ["Voorraadwaarde hulpstoffen", euro(vr.hulpTot || 0)], ["Kritische artikelen", String((vr.kritisch || []).length)]], columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "right" } } });
    if ((vr.kritisch || []).length) tbl({ head: [["Soort", "Artikel", "Voorraad", "Minimum"]], body: vr.kritisch.map((x) => [x.cat, x.naam, kgf(x.voorraad), kgf(x.min)]), columnStyles: { 2: { halign: "right" }, 3: { halign: "right" } } });
    if ((vr.prijsOntw || []).length) tbl({ head: [["Artikel", "€/kg nu", "Eerder", "Verschil"]], body: vr.prijsOntw.map((x) => [x.naam, euro(x.nu), x.eerder != null ? euro(x.eerder) : "—", x.delta == null ? "—" : (x.delta > 0 ? "+" : "") + euro(x.delta)]), columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } } });
    if (S.inkoop !== false && (inkoop || []).length) tbl({ head: [["Grondstof", "Leverancier", "kg", "€/kg", "Totaal"]], body: inkoop.map((x) => [x.naam, x.leverancier || "—", kgf(x.kg), euro(x.prijs), euro(x.totaal)]), columnStyles: { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } } });
    if (S.inkoopHulp !== false && (inkoopHulp || []).length) tbl({ head: [["Type", "Hulpstof", "Leverancier", "kg", "€/kg", "Totaal"]], body: inkoopHulp.map((x) => [x.type || "—", x.naam, x.leverancier || "—", kgf(x.kg), euro(x.prijs), euro(x.totaal)]), columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } } });
    toel("voorraad");
  }
  // 10. Investeringen
  if (S.investeringen !== false) {
    heading("10 · Investeringen & projecten", ((man.investeringen || []).filter((x) => x && (x.onderwerp || x.status)).length) || 1);
    const inv = (man.investeringen || []).filter((x) => x && (x.onderwerp || x.status));
    if (inv.length) tbl({ head: [["Onderwerp", "Status", "Begroot", "Werkelijk", "Verantw.", "Deadline", "Actie"]], body: inv.map((x) => [x.onderwerp || "", x.status || "", x.begroot ? euro(Number(x.begroot)) : "—", x.werkelijk ? euro(Number(x.werkelijk)) : "—", x.verantwoordelijke || "", x.deadline || "", x.actie || ""]), columnStyles: { 2: { halign: "right" }, 3: { halign: "right" } } });
    else { doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(100, 116, 139); doc.text("Geen lopende investeringen of projecten vastgelegd.", marginX, y); y += 6; }
    toel("investeringen");
  }
  // 12. Trends
  if (S.trends !== false && (trends || []).length >= 3) {
    heading(`Trends (laatste ${trends.length} weken)`, 8);
    const tCfg = (model.trendCfg || {}).kpis || null;
    const defs = [["Productie (kg)", "kg", kgf], ["Kostprijs/kg", "kostprijsKg", euro], ["Yield", "yieldPct", pct], ["OEE", "oee", pct], ["Uitval", "uitvalPct", pct], ["Omzet", "omzet", euro], ["Marge", "marge", euro], ["kg/manuur", "kgPerManuur", kgf], ["Pers.kosten/kg", "persKostPerKg", euro]].filter(([, k]) => (tCfg ? tCfg[k] !== false : true) && trends.some((t) => t[k] != null));
    tbl({ head: [["KPI", ...trends.map((t) => "wk " + t.week)]], body: defs.map(([l, k, f]) => [l, ...trends.map((t) => t[k] == null ? "—" : f(t[k]))]), columnStyles: Object.fromEntries(trends.map((_, i) => [i + 1, { halign: "right" }])) });
    toel("trends");
  }
  // 11. Managementacties (laatst, duidelijk)
  if (S.acties !== false) {
    heading("11 · Managementacties");
    const ac = man.acties || {};
    paragraph("Belangrijkste afwijkingen deze week", ac.afwijkingen);
    paragraph("Oorzaak", ac.oorzaak);
    paragraph("Genomen actie / acties komende week", ac.actiesKomend);
    paragraph("Besluit / ondersteuning nodig van directie", ac.besluiten);
  }
  finalize();

  function persSection() {
    const wU = personeel.totaal.werkUren;
    tbl({
      head: [["Afdeling", "Medew.", "Gewerkt", "Gepland", "Afw. u", "Kosten"]],
      body: [...personeel.bureaus.map((b) => [b.bureau, String(b.mensen), uur(b.werkUren), uur(b.planUren), uur(b.werkUren - b.planUren), euro(b.werkKost)]),
      [{ content: "Totaal", styles: { fontStyle: "bold" } }, { content: String(personeel.bureaus.reduce((s, b) => s + b.mensen, 0)), styles: { halign: "right", fontStyle: "bold" } }, { content: uur(wU), styles: { halign: "right", fontStyle: "bold" } }, { content: uur(personeel.totaal.planUren), styles: { halign: "right", fontStyle: "bold" } }, { content: uur(personeel.totaal.diffUren), styles: { halign: "right", fontStyle: "bold" } }, { content: euro(personeel.totaal.werkKost), styles: { halign: "right", fontStyle: "bold" } }]],
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
    });
  }
  function persExtraSection() {
    const mp = man.pers || {};
    const rows = [];
    if (prodKg != null && personeel.totaal.werkUren > 0) rows.push(["kg per gewerkt uur", kgf(prodKg / personeel.totaal.werkUren) + "/u"]);
    if (mp.fte !== "" && mp.fte != null) rows.push(["Aantal FTE (eigen)", String(mp.fte)]);
    if (mp.uzkAantal !== "" && mp.uzkAantal != null) rows.push(["Uitzendkrachten", String(mp.uzkAantal)]);
    if (mp.overurenUur !== "" && mp.overurenUur != null) rows.push(["Overuren", uur(Number(mp.overurenUur))]);
    if (mp.ziekteverzuim !== "" && mp.ziekteverzuim != null) rows.push(["Ziekteverzuim", pct(Number(mp.ziekteverzuim))]);
    if (mp.vakantie !== "" && mp.vakantie != null) rows.push(["Vakantie", uur(Number(mp.vakantie))]);
    if (rows.length) tbl({ body: rows, columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "right" } } });
  }
  function afkeurSection() {
    heading("Materiaalafkeur", (afkeur && afkeur.length ? afkeur.length + 1 : 1));
    if (afkeur && afkeur.length) tbl({ head: [["Categorie", "Afgekeurd (kg)"]], body: [...afkeur.map((a) => [a.categorie, kgf(a.kg)]), [{ content: "Totaal", styles: { fontStyle: "bold" } }, { content: kgf(afkeurTot), styles: { halign: "right", fontStyle: "bold" } }]], columnStyles: { 1: { halign: "right" } } });
    else { doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(100, 116, 139); doc.text("Geen afgekeurd materiaal geregistreerd deze week.", marginX, y); y += 6; }
  }
  function finalize() {
    const o = ondertekening || {};
    if (y > 250) { doc.addPage(); y = 30; }
    y += 10;
    doc.setDrawColor(148, 163, 184); doc.setLineWidth(0.3); doc.line(marginX, y, marginX + 70, y); y += 5;
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(15, 23, 42); doc.text(o.naam || "", marginX, y); y += 4.5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text(`${o.functie || ""} · ${o.bedrijf || ""}`, marginX, y); y += 4; doc.text(`Datum: ${nu}`, marginX, y);
    if (model.organogramImg) addOrganogramPage();
    const total = doc.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      doc.setFontSize(7.5); doc.setTextColor(148, 163, 184);
      doc.text(`Vertrouwelijk — intern directiegebruik · pagina ${i}/${total}`, pw / 2, ph - 5, { align: "center" });
    }
    doc.save(filename);
  }
  function addOrganogramPage() {
    doc.addPage("a4", "landscape");
    const lw = doc.internal.pageSize.getWidth();
    const lh = doc.internal.pageSize.getHeight();
    const m = 12;
    doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(28, 100, 176);
    doc.text("Organogram — Clazing Convenience Food B.V.", m, 15);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(nu, lw - m, 15, { align: "right" });
    doc.setDrawColor(28, 100, 176); doc.setLineWidth(0.5); doc.line(m, 18, lw - m, 18);
    const top = 24;
    const wh = model.organogramWH || [4, 3];
    const cw = wh[0] || 4, ch = wh[1] || 3;
    const maxW = lw - 2 * m, maxH = lh - top - m;
    let iw = maxW, ih = ch * (iw / cw);
    if (ih > maxH) { ih = maxH; iw = cw * (ih / ch); }
    try { doc.addImage(model.organogramImg, "PNG", m + (maxW - iw) / 2, top, iw, ih); } catch (e) { /* organogram optioneel */ }
  }
}
